import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getHourRatesAsync, deleteHourRateAsync } from "../../../../slices/offer-calculation-hour-rate";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { PlusIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { HourRatesTable } from "../../../../components/dashboard/offers/hour-rates/hour-rates-table";
import ProConfirmation from "../../../../components/utils/ProConfirmation";
import toast from "react-hot-toast";
import { updateUrlWithException } from "../../../../components/Utils";
import { useSettings } from "../../../../contexts/settings-context";

export const OfferCalculationHourRatesList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const [controller, setController] = useState({
    filters: [],
    page: searchParams.get("page") ? parseInt(searchParams.get("page")) : 1,
    per_page: searchParams.get("per_page") ? parseInt(searchParams.get("per_page")) : 10,
    query: searchParams.get("query") || "",
    sort: searchParams.get("sort") || "ASC",
    sortBy: searchParams.get("sortBy") || "row_order",
    active: searchParams.get("active") || null,
  });

  const itemState = useSelector((store) => {
    return store.offerCalculationHourRates;
  });

  const { hourRates: data, isLoading, totalCount, currentCount } = itemState;

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [hourRateToDelete, setHourRateToDelete] = useState(null);

  // Update all params from controller except filters
  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        active: controller.active,
      },
      "filters"
    );
  }, [controller]);

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    // Convert ModuleFilter format to backend format
    const backendFilters = [];

    // Handle active filter (inactive_hour_rates checkbox)
    if (filters.inactive_hour_rates) {
      backendFilters.push({
        name: "active",
        value: "false",
      });
    } else {
      backendFilters.push({
        name: "active",
        value: "true",
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query || controller.query,
      filters: backendFilters,
      active: filters.inactive_hour_rates ? "false" : "true",
    });
  };

  const debouncedDispatchAction = debounce((controller) => {
    // Convert controller to API format
    const apiParams = {
      page: controller.page,
      per_page: controller.per_page,
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
      active: controller.active,
    };
    dispatch(getHourRatesAsync(apiParams));
  }, 250);

  useEffect(() => {
    debouncedDispatchAction(controller);

    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const handleDelete = (hourRate, e) => {
    e.stopPropagation();
    setHourRateToDelete(hourRate);
    setDeleteConfirmationOpen(true);
  };

  const confirmDelete = async () => {
    if (hourRateToDelete) {
      try {
        await dispatch(deleteHourRateAsync(hourRateToDelete.id)).unwrap();
        toast.success(t("Timesats ble slettet"));
        setDeleteConfirmationOpen(false);
        setHourRateToDelete(null);
        // Refresh the list
        const apiParams = {
          page: controller.page,
          per_page: controller.per_page,
          query: controller.query,
          sort: controller.sort,
          sortBy: controller.sortBy,
          active: controller.active,
        };
        dispatch(getHourRatesAsync(apiParams));
      } catch (error) {
        toast.error(t("Kunne ikke slette timesats"));
      }
    }
  };

  // Custom checkbox button filters for hour rates
  const customCheckboxButtonFilters = [
    {
      key: "inactive_hour_rates",
      label: t("Inaktive timesatser"),
      icon: ExclamationTriangleIcon,
      defaultValue: false,
    }
  ];

  // Convert backend filters to ModuleFilter format
  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query,
      inactive_hour_rates: controller.active === "false",
    };

    return initialFilters;
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={[]}
        title={t("Tilbuds kalkyle timesatser")}
        mainButton={{
          label: t("Ny timesats"),
          action: () => navigate("/dashboard/offers/hour-rates/new"),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder={t("Søk timesatser...")}
              filterTitle={t("Filtrer timesatser")}
              customCheckboxButtonFilters={customCheckboxButtonFilters}
            />

            <HourRatesTable
              itemState={itemState}
              itemsCount={totalCount}
              currentCount={currentCount}
              page={controller.page}
              perPage={controller.per_page}
              onPageChange={handlePageChange}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </DashboardHeader>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("Denne timesatsen vil bli fjernet for godt og kan ikke reverseres")}
        action="delete"
        module="offer_calculation_hour_rate"
        yesCallback={() => confirmDelete()}
        cancelCallback={() => {
          setDeleteConfirmationOpen(false);
          setHourRateToDelete(null);
        }}
      />
    </>
  );
};

